import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../utils/cn.js";

type Surface = "paper" | "cream-2" | "ink";

const surfaces: Record<Surface, string> = {
  paper:    "bg-paper border border-line text-ink",
  "cream-2": "bg-cream-2 border border-transparent text-ink",
  ink:      "bg-ink border border-transparent text-cream",
};

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Padding shorthand. `none | sm (14) | md (18) | lg (24)`. Default `md`. */
  padding?: "none" | "sm" | "md" | "lg";
  surface?: Surface;
}

const padMap = {
  none: "",
  sm: "p-3.5",
  md: "p-[18px]",
  lg: "p-6",
} as const;

/**
 * Card — primary container surface.
 *
 * Brand spec: 18px radius, hairline border on paper, no shadow at rest.
 * Cream-2 surface used for quiet/quotation contexts (daily wisdom,
 * gentle insights). Ink surface for the "one tiny action today" panel
 * — high-contrast, mustard-eyebrow only.
 */
export const Card = ({
  padding = "md",
  surface = "paper",
  className,
  children,
  ...rest
}: CardProps) => (
  <div
    className={cn(
      "rounded-lg",
      surfaces[surface],
      padMap[padding],
      className,
    )}
    {...rest}
  >
    {children}
  </div>
);

export const CardHeader = ({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
}) => (
  <div className="flex items-start justify-between gap-4">
    <div className="min-w-0">
      {eyebrow && (
        <div className="font-mono text-eyebrow font-semibold uppercase text-muted">
          {eyebrow}
        </div>
      )}
      <h3 className="font-serif text-h3 font-medium text-ink">{title}</h3>
      {description && (
        <p className="mt-1 text-small text-muted">{description}</p>
      )}
    </div>
    {action}
  </div>
);
